import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import type { WorkflowStepInstanceAdditionalInformation } from "@/types";

export type FeedbackType = "additionalInformation" | "agentResponse";
export type FileScanStatusType =
  | "idle"
  | "scanning"
  | "completed"
  | "malicious"
  | "error";

export interface FileScanStatusData {
  fileId: string | null;
  fileName: string;
  status: FileScanStatusType;
  isMalicious: boolean | null;
  error: string | null;
}

export interface FieldState {
  formValues: string;
  replyOpen: boolean;
  markedAsNotRequired: boolean;
  files: Array<{
    fileId: string | null;
    fileName: string;
    status: FileScanStatusType;
    isMalicious: boolean | null;
    error: string | null;
  }>;
}

export interface FeedbackFormStateByType {
  byFieldId: Record<string, FieldState>;
}

export interface FeedbackFormState {
  additionalInformation: FeedbackFormStateByType;
  agentResponse: FeedbackFormStateByType;
}

const initialFieldState: FieldState = {
  formValues: "",
  replyOpen: false,
  markedAsNotRequired: false,
  files: [],
};

const initialFeedbackFormStateByType: FeedbackFormStateByType = {
  byFieldId: {},
};

const initialState: FeedbackFormState = {
  additionalInformation: initialFeedbackFormStateByType,
  agentResponse: initialFeedbackFormStateByType,
};

export const feedbackFormSlice = createSlice({
  name: "feedbackForm",
  initialState,
  reducers: {
    updateFieldValue: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
        value: string;
      }>,
    ) => {
      const { feedbackType, fieldId, value } = action.payload;
      const fieldCollection = state[feedbackType];

      if (!fieldCollection.byFieldId[fieldId]) {
        fieldCollection.byFieldId[fieldId] = { ...initialFieldState };
      }
      fieldCollection.byFieldId[fieldId].formValues = value;
    },
    openReply: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
      }>,
    ) => {
      const { feedbackType, fieldId } = action.payload;
      const fieldCollection = state[feedbackType];

      if (!fieldCollection.byFieldId[fieldId]) {
        fieldCollection.byFieldId[fieldId] = { ...initialFieldState };
      }
      fieldCollection.byFieldId[fieldId].replyOpen = true;
    },
    closeReply: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
      }>,
    ) => {
      const { feedbackType, fieldId } = action.payload;
      const fieldCollection = state[feedbackType];

      if (!fieldCollection.byFieldId[fieldId]) {
        fieldCollection.byFieldId[fieldId] = { ...initialFieldState };
      }
      fieldCollection.byFieldId[fieldId].replyOpen = false;
    },
    toggleRequired: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
        isRequired: boolean;
      }>,
    ) => {
      const { feedbackType, fieldId, isRequired } = action.payload;
      const fieldCollection = state[feedbackType];

      if (!fieldCollection.byFieldId[fieldId]) {
        fieldCollection.byFieldId[fieldId] = { ...initialFieldState };
      }
      fieldCollection.byFieldId[fieldId].markedAsNotRequired = !isRequired;
    },
    addFileToField: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
        fileName: string;
      }>,
    ) => {
      const { feedbackType, fieldId, fileName } = action.payload;
      const fieldCollection = state[feedbackType];

      if (!fieldCollection.byFieldId[fieldId]) {
        fieldCollection.byFieldId[fieldId] = { ...initialFieldState };
      }

      const existingIndex = fieldCollection.byFieldId[fieldId].files.findIndex(
        (f) => f.fileName === fileName,
      );

      if (existingIndex === -1) {
        fieldCollection.byFieldId[fieldId].files.push({
          fileId: null,
          fileName,
          status: "idle",
          isMalicious: null,
          error: null,
        });
      }
    },
    startFileScan: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
        fileName: string;
      }>,
    ) => {
      const { feedbackType, fieldId, fileName } = action.payload;
      const fieldCollection = state[feedbackType];
      const field = fieldCollection.byFieldId[fieldId];

      if (field) {
        const file = field.files.find((f) => f.fileName === fileName);
        if (file) {
          file.status = "scanning";
        }
      }
    },
    fileScanCompleted: (
      state,
      action: PayloadAction<{
        fileName: string;
        fileId: string;
        isMalicious: boolean;
      }>,
    ) => {
      const { fileName, fileId, isMalicious } = action.payload;

      // Search both namespaces for the file
      Object.entries(state).forEach(([, feedbackTypeState]) => {
        Object.entries(feedbackTypeState.byFieldId).forEach(([, field]) => {
          const fileEntry = field.files.find((f) => f.fileName === fileName);
          if (fileEntry) {
            fileEntry.fileId = fileId;
            fileEntry.isMalicious = isMalicious;
            fileEntry.status = isMalicious ? "malicious" : "completed";
          }
        });
      });
    },
    fileScanFailed: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
        fileName: string;
        error: string;
      }>,
    ) => {
      const { feedbackType, fieldId, fileName, error } = action.payload;
      const field = state[feedbackType].byFieldId[fieldId];

      if (field) {
        const file = field.files.find((f) => f.fileName === fileName);
        if (file) {
          file.status = "error";
          file.error = error;
        }
      }
    },
    removeFile: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
        fileId: string | null;
      }>,
    ) => {
      const { feedbackType, fieldId, fileId } = action.payload;
      const field = state[feedbackType].byFieldId[fieldId];

      if (field && fileId) {
        field.files = field.files.filter((f) => f.fileId !== fileId);
      }
    },
    clearFieldState: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldId: string;
      }>,
    ) => {
      const { feedbackType, fieldId } = action.payload;
      delete state[feedbackType].byFieldId[fieldId];
    },
    clearAllFieldState: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
      }>,
    ) => {
      const { feedbackType } = action.payload;
      state[feedbackType].byFieldId = {};
    },
    initialiseFields: (
      state,
      action: PayloadAction<{
        feedbackType: FeedbackType;
        fieldIds: string[];
      }>,
    ) => {
      const { feedbackType, fieldIds } = action.payload;
      const fieldCollection = state[feedbackType];

      fieldIds.forEach((fieldId) => {
        if (!fieldCollection.byFieldId[fieldId]) {
          fieldCollection.byFieldId[fieldId] = { ...initialFieldState };
        }
      });
    },
  },
});

export const {
  updateFieldValue,
  openReply,
  closeReply,
  toggleRequired,
  addFileToField,
  startFileScan,
  fileScanCompleted,
  fileScanFailed,
  removeFile,
  clearFieldState,
  clearAllFieldState,
  initialiseFields,
} = feedbackFormSlice.actions;

// Selectors
export const selectFeedbackFormByType = (
  state: RootState,
  feedbackType: FeedbackType,
) => state.feedbackForm[feedbackType];

export const selectFieldState = (
  state: RootState,
  feedbackType: FeedbackType,
  fieldId: string,
) => state.feedbackForm[feedbackType].byFieldId[fieldId] ?? null;

export const selectFileScanStatuses = (
  state: RootState,
  feedbackType: FeedbackType,
  fieldId: string,
) => {
  const field = state.feedbackForm[feedbackType].byFieldId[fieldId];
  return field?.files ?? [];
};

export const selectIsFieldScanInProgress = (
  state: RootState,
  feedbackType: FeedbackType,
  fieldId: string,
) => {
  const field = state.feedbackForm[feedbackType].byFieldId[fieldId];
  return field?.files.some((f) => f.status === "scanning") ?? false;
};

export const selectFieldHasMaliciousFile = (
  state: RootState,
  feedbackType: FeedbackType,
  fieldId: string,
) => {
  const field = state.feedbackForm[feedbackType].byFieldId[fieldId];
  return field?.files.some((f) => f.isMalicious) ?? false;
};

export const selectIsSubmitDisabled = (
  state: RootState,
  feedbackType: FeedbackType,
  fieldIds: string[],
) => {
  const feedbackTypeState = state.feedbackForm[feedbackType];

  return fieldIds.some((fieldId) => {
    const field = feedbackTypeState.byFieldId[fieldId];
    if (!field) return false;

    // Block if scan in progress
    if (field.files.some((f) => f.status === "scanning")) {
      return true;
    }

    // Block if any file is malicious
    if (field.files.some((f) => f.isMalicious)) {
      return true;
    }

    return false;
  });
};

export const selectCanSubmitAdditionalInformationStep = (
  state: RootState,
  requests: Array<{
    fieldId: string;
    request: WorkflowStepInstanceAdditionalInformation;
  }>,
) => {
  const feedbackTypeState = state.feedbackForm.additionalInformation;

  return requests.every(({ fieldId, request }) => {
    const field = feedbackTypeState.byFieldId[fieldId];

    const hasScanningFile = field?.files.some(
      (file) => file.status === "scanning",
    );
    if (hasScanningFile) {
      return false;
    }

    const hasMaliciousFile = field?.files.some((file) => file.isMalicious);
    if (hasMaliciousFile) {
      return false;
    }

    const activeResponseText = (
      field && !field.replyOpen ? field.formValues : (request.userInput ?? "")
    ).trim();
    const isRequired = field ? !field.markedAsNotRequired : request.isRequired;

    if (!isRequired) {
      return true;
    }

    const hasCompletedUploadedFile =
      (field && !field.replyOpen
        ? field.files.some(
            (file) => file.status === "completed" && !file.isMalicious,
          )
        : false) || false;

    const hasSavedAttachment = (request.attachments?.length ?? 0) > 0;

    return (
      activeResponseText.length > 0 ||
      hasCompletedUploadedFile ||
      hasSavedAttachment
    );
  });
};

export const feedbackFormReducer = feedbackFormSlice.reducer;
